import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router';
import _ from 'lodash';
import { noop } from 'helpers/GlobalHelper';

import Style from './Dropdown.module.scss';

/**
 * Dropdown Component
 *
 * @param {Object} theme       - Theme override sent through CSS Themr
 * @param {Object} expandedHeaderCopy - Style override for the header shown when the dropdown is expanded
 * @param {Array} items        - array of objects {name: 'test', onClick: ()=>{}}
 * @param {Function} onOpenDropdown - function to be called when dropdown opens
 * @param {Function} onCloseDropdown - function to be called when dropdown closes
 * @param {String} defaultText - the deault text to display when no item is selected
 * @param {Function} getIcon - function returns icon that has onClick event passed into it
 * @param {Boolean} expanded - is the dropdown expanded?
 * @param {String} getSelected - Returns the name of the clicked menu item
 * @param {String} selected - the currently selected item
 * @param {Bool} preserveTitle - Preserve the original title for the dropdown even when items are selected
 * @param {String} useLabelDetail - Arbitrary text that is adjacent to the item name within the menu
 * @param {Bool} isActive - Indicates that the dropdown is the last one the user interacted with
 * @param {Bool} isError - is there an error with the current field?
 * @returns {Component}        - React Component
 */
class Dropdown extends Component {
  constructor(props) {
    super(props);
    this.closeWhenOutOfBounds = this.closeWhenOutOfBounds.bind(this);
  }

  closeWhenOutOfBounds(e) {
    const { onCloseDropdown } = this.props;
    const height = _.get(e.target.getBoundingClientRect(), 'top');
    const mousePositionOnClick = _.get(e, 'clientY');
    return height > mousePositionOnClick ? onCloseDropdown() : null;
  }

  renderMenuItem(item) {
    if (!item) return null;
    const {
      linkTo,
      name: originalName,
      group,
      onClickParams,
      onClick,
      labelDetail,
      isSubmenu,
      isSelected,
    } = item;
    const name = originalName === 'Clips & Extras' ? 'Clips' : originalName;
    const {
      accessibilityOverride,
      useLabelDetail,
      getSelected,
      onCloseDropdown,
      theme,
      openSubmenu,
      selected,
      getUrlTarget,
    } = this.props;
    const firstItemName = _.get(item, '[0].name');
    let contentClasses = classNames(theme.itemContent, Style.itemContent);
    let itemClasses = classNames(theme.item, Style.item);
    if (isSelected || selected === name || (!selected && firstItemName === name)) {
      contentClasses = classNames(contentClasses, theme.itemContentActive, Style.itemContentActive);
      itemClasses = classNames(itemClasses, theme.itemActive);
    }
    let target = null;
    let rel = null;
    if (getUrlTarget) {
      target = _.get(getUrlTarget(linkTo), 'target');
      rel = _.get(getUrlTarget(linkTo), 'rel');
    }
    // div's in some context will not be tabbable and will need to be for accessibility
    const content = accessibilityOverride ? (
      <button className={contentClasses}>
        {name}
        {useLabelDetail && <span style={{ marginLeft: 10 }}>{labelDetail}</span>}
      </button>
    ) : (
      <div className={contentClasses}>
        {name}
        {useLabelDetail && <span style={{ marginLeft: 10 }}>{labelDetail}</span>}
      </div>
    );
    return (
      <Link
        key={name}
        className={itemClasses}
        to={linkTo}
        target={target}
        rel={rel}
        onClick={() => {
          if (onClick) {
            onClick(onClickParams);
          }

          if (isSubmenu) {
            openSubmenu(group);
          }

          if (getSelected) {
            getSelected(name);
          }

          if (onCloseDropdown) {
            onCloseDropdown();
          }
        }}
      >
        {content}
      </Link>
    );
  }

  renderGroupedItems(items) {
    return _.map(items, item => this.renderMenuItem(item));
  }

  render() {
    const {
      accessibilityOverride,
      className,
      defaultText,
      displayResults,
      expanded,
      expandedHeaderCopy,
      getIcon,
      items,
      onCloseDropdown,
      onOpenDropdown,
      selected,
      showMobileCancel,
      theme,
      isActive,
      preserveTitle,
      isError,
      disabled,
    } = this.props;
    const selectedText = selected || defaultText;
    let itemTags;

    if (!_.isEmpty(items) && displayResults) {
      const partitionedItems = _.partition(items, ['group', true]);
      const singleMenuItems = _.map(partitionedItems[0], (item, index) =>
        this.renderMenuItem(item, index),
      );
      const groupedItems = this.renderGroupedItems(partitionedItems[1]);

      itemTags = [...singleMenuItems, ...groupedItems];

      if (showMobileCancel) {
        itemTags.push(
          <Link
            key="cancel"
            className={classNames([theme.item, theme.cancelButton, Style.item])}
            onClick={() => {
              onCloseDropdown();
            }}
          >
            <div className={classNames([theme.itemContent, Style.itemContent])}>CANCEL</div>
          </Link>,
        );
      }
    }
    const onClick = () => {
      if (!disabled) return expanded ? onCloseDropdown() : onOpenDropdown();
      return null;
    };
    const itemContainerClass = expanded
      ? classNames(theme.itemContainer, Style.itemContainer)
      : classNames(theme.hideItemContainer, Style.hideItemContainer);
    const itemContainer = displayResults ? (
      <div className={classNames([itemContainerClass, expandedHeaderCopy])}>
        <div className={theme.innerItemContainer}>{itemTags}</div>
      </div>
    ) : null;
    let containerClasses = classNames(className, theme.container, Style.container);
    let headerClasses = classNames(theme.header, Style.header);
    const headerInnerContainerClasses = [
      classNames(theme.headerInnerContainer, Style.headerInnerContainer),
    ];
    let selectedTextClasses = isActive
      ? classNames(theme.active, theme.selectedText, Style.selectedText)
      : classNames(theme.selectedText, Style.selectedText);

    if (isError) headerInnerContainerClasses.push(theme.headerInnerContainerError);
    if (expanded) {
      containerClasses = classNames(containerClasses, theme.containerOpen);
      headerClasses = classNames(headerClasses, theme.headerOpen, Style.headerOpen);
      selectedTextClasses = classNames(
        selectedTextClasses,
        theme.selectedTextOpen,
        Style.selectedTextOpen,
      );
      headerInnerContainerClasses.push(theme.headerInnerContainerOpen);
    }
    if (disabled) {
      selectedTextClasses = classNames(theme.selectedText, Style.selectedText, theme.disabledText);
      headerClasses = classNames(theme.header, Style.header, theme.headerDisabled);
    }

    // div's in some context will not be tabbable and will need to be for accessibility
    const header = accessibilityOverride ? (
      <button className={classNames(headerInnerContainerClasses)}>
        <Link className={selectedTextClasses} onClick={onClick} data-testid="dropdown-item">
          {preserveTitle ? defaultText : selectedText}
        </Link>
        {getIcon(onClick)}
      </button>
    ) : (
      <div className={classNames(headerInnerContainerClasses)}>
        <Link className={selectedTextClasses} onClick={onClick} data-testid="dropdown-item">
          {preserveTitle ? defaultText : selectedText}
        </Link>
        {getIcon(onClick)}
      </div>
    );
    return (
      <div
        className={containerClasses}
        data-testid="dropdown"
        onClick={expanded ? this.closeWhenOutOfBounds : noop}
      >
        <div className={headerClasses} onClick={onClick}>
          {header}
        </div>
        {itemContainer}
      </div>
    );
  }
}

Dropdown.defaultProps = {
  accessibilityOverride: false,
  className: null,
  defaultText: '',
  disabled: null,
  displayResults: null,
  expanded: null,
  expandedHeaderCopy: {},
  getIcon: noop,
  getSelected: null,
  getUrlTarget: noop,
  isActive: null,
  isError: null,
  items: [],
  onCloseDropdown: noop,
  onOpenDropdown: noop,
  openSubmenu: noop,
  preserveTitle: null,
  selected: null,
  showMobileCancel: null,
  useLabelDetail: null,
};

Dropdown.propTypes = {
  accessibilityOverride: PropTypes.bool,
  className: PropTypes.string,
  defaultText: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  disabled: PropTypes.bool,
  displayResults: PropTypes.bool,
  expanded: PropTypes.bool,
  expandedHeaderCopy: PropTypes.shape({}),
  getIcon: PropTypes.func,
  getSelected: PropTypes.string,
  getUrlTarget: PropTypes.func,
  isActive: PropTypes.bool,
  isError: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  onCloseDropdown: PropTypes.func,
  onOpenDropdown: PropTypes.func,
  openSubmenu: PropTypes.func,
  preserveTitle: PropTypes.bool,
  selected: PropTypes.string,
  showMobileCancel: PropTypes.bool,
  theme: PropTypes.shape({}).isRequired,
  useLabelDetail: PropTypes.string,
};

export default Dropdown;
